import React, { useCallback, useEffect, useState } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/UsersPage.module.css';

const emptyForm = {
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user',
  status: 'active',
  dailyRate: '',
};

const emptyEditForm = {
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user',
  status: 'active',
  dailyRate: '',
};

const fmtRate = (val) => {
  const n = Number(val);
  if (!n && n !== 0) return '—';
  return `RM ${n.toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const UsersPage = () => {
  const { can } = useAuth();
  const { t } = useLocalization();
  const canViewFinancials = can('financials', 'view');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [editingUserId, setEditingUserId] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      alert(error.response?.data?.message || t('users_fetch_failed', 'Failed to fetch users'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.create({
        ...createForm,
        dailyRate: ['user','supervisor'].includes(createForm.role) ? createForm.dailyRate : undefined,
      });
      setCreateForm(emptyForm);
      setShowCreate(false);
      await fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || t('users_create_failed', 'Failed to create user'));
    }
  };

  const openEditModal = (user) => {
    setEditForm({
      id: user.id,
      username: user.username || '',
      email: user.email || '',
      password: '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      role: user.role || 'user',
      status: user.status || 'active',
      dailyRate: user.daily_rate !== undefined ? String(user.daily_rate) : '',
    });
    setShowEdit(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      setEditingUserId(editForm.id);
      await usersAPI.update(editForm.id, {
        username: editForm.username,
        email: editForm.email,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        role: editForm.role,
        status: editForm.status,
        password: editForm.password || undefined,
        dailyRate: ['user','supervisor'].includes(editForm.role) ? editForm.dailyRate : undefined,
      });
      setShowEdit(false);
      setEditForm(emptyEditForm);
      await fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || t('users_update_failed', 'Failed to update user'));
    } finally {
      setEditingUserId(null);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm(t('users_delete_confirm', 'Delete this user? This action will deactivate the account.'))) return;

    try {
      setEditingUserId(id);
      await usersAPI.deactivate(id);
      await fetchUsers();
    } catch (error) {
      alert(error.response?.data?.message || t('users_deactivate_failed', 'Failed to deactivate user'));
    } finally {
      setEditingUserId(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>{t('users_page_title', 'User Maintenance')}</h1>
        {can('users', 'create') && (
          <button className={styles.createBtn} onClick={() => setShowCreate(true)}>{t('users_add_user', '+ Add User')}</button>
        )}
      </div>

      {loading ? (
        <p>{t('users_loading', 'Loading users...')}</p>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('users_username', 'Username')}</th>
                <th>{t('users_name', 'Name')}</th>
                <th>{t('users_email', 'Email')}</th>
                <th>{t('users_role', 'Role')}</th>
                <th>{t('users_status', 'Status')}</th>
                {canViewFinancials && <th>{t('users_daily_rate', 'Daily Rate')}</th>}
                <th>{t('users_actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.first_name} {user.last_name}</td>
                  <td>{user.email}</td>
                  <td>
                    {can('users', 'edit') ? (
                      <span>{user.role}</span>
                    ) : (
                      user.role
                    )}
                  </td>
                  <td>
                    {can('users', 'edit') ? (
                      <span>{user.status}</span>
                    ) : (
                      user.status
                    )}
                  </td>
                  {canViewFinancials && (
                    <td className={styles.currencyCell}>
                      {['user','supervisor'].includes(user.role) ? fmtRate(user.daily_rate) : <span style={{color:'#94a3b8'}}>—</span>}
                    </td>
                  )}
                  <td className={styles.actionsCell}>
                    {can('users', 'edit') && (
                      <button
                        className={styles.editBtn}
                        onClick={() => openEditModal(user)}
                        disabled={editingUserId === user.id}
                      >
                        {t('users_edit', 'Edit')}
                      </button>
                    )}
                    {can('users', 'delete') && user.status === 'active' && (
                      <button
                        className={styles.deactivateBtn}
                        onClick={() => handleDeactivate(user.id)}
                        disabled={editingUserId === user.id}
                      >
                        {t('users_delete', 'Delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showEdit && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('users_modal_edit_title', 'Edit User')}</h2>
            <form onSubmit={handleUpdate}>
              <div className={styles.formGrid}>
                <div className={styles.formField}>
                  <label>{t('users_username_required', 'Username *')}</label>
                  <input
                    value={editForm.username}
                    onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_email_required', 'Email *')}</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_first_name', 'First Name')}</label>
                  <input
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_last_name', 'Last Name')}</label>
                  <input
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_role_required', 'Role *')}</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <option value="admin">admin</option>
                    <option value="supervisor">supervisor</option>
                    <option value="user">user</option>
                  </select>
                </div>
                <div className={styles.formField}>
                  <label>{t('users_status_required', 'Status *')}</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="active">{t('common_status_active', 'Active')}</option>
                    <option value="inactive">{t('common_status_inactive', 'Inactive')}</option>
                  </select>
                </div>
                <div className={styles.formField}>
                  <label>{t('users_new_password_optional', 'New Password (optional)')}</label>
                  <input
                    type="password"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  />
                </div>
                {canViewFinancials && ['user','supervisor'].includes(editForm.role) && (
                  <div className={styles.formField}>
                    <label>{t('users_daily_rate_rm', 'Daily Rate (RM)')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={t('common_amount_placeholder', '0.00')}
                      value={editForm.dailyRate}
                      onChange={(e) => setEditForm({ ...editForm, dailyRate: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.createBtn} disabled={editingUserId === editForm.id}>{t('common_save', 'Save')}</button>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowEdit(false)}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreate && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h2>{t('users_modal_add_title', 'Add User')}</h2>
            <form onSubmit={handleCreate}>
              <div className={styles.formGrid}>
                <div className={styles.formField}>
                  <label>{t('users_username_required', 'Username *')}</label>
                  <input
                    value={createForm.username}
                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_email_required', 'Email *')}</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_first_name', 'First Name')}</label>
                  <input
                    value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_last_name', 'Last Name')}</label>
                  <input
                    value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                  />
                </div>
                <div className={styles.formField}>
                  <label>{t('users_role_required', 'Role *')}</label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  >
                    <option value="admin">admin</option>
                    <option value="supervisor">supervisor</option>
                    <option value="user">user</option>
                  </select>
                </div>
                {canViewFinancials && ['user','supervisor'].includes(createForm.role) && (
                  <div className={styles.formField}>
                    <label>{t('users_daily_rate_rm', 'Daily Rate (RM)')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={t('common_amount_placeholder', '0.00')}
                      value={createForm.dailyRate}
                      onChange={(e) => setCreateForm({ ...createForm, dailyRate: e.target.value })}
                    />
                  </div>
                )}
                <div className={styles.formField}>
                  <label>{t('users_password_required', 'Password *')}</label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className={styles.modalButtons}>
                <button type="submit" className={styles.createBtn}>{t('users_create', 'Create')}</button>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowCreate(false)}>{t('common_cancel', 'Cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
